import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FaArrowLeft, FaInfoCircle } from "react-icons/fa";
import { BsCloudUpload, BsFileEarmarkPdf } from "react-icons/bs";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
// import StatusChip, { statusChipType } from "../../../Common/StatusChip";
import { InputField, SelectField, TextAreaField } from "../../../Common/FormComponents";
import { getallWorkOrders } from "../../../../Redux/actions/Operationsactions/getworkorders";
import { getWorkOrderById } from "../../../../Redux/actions/Operationsactions/getworkorderbyid";
import { getPopulateUsers } from "../../../../Redux/actions/accountsactions/getContractComments";
import { AddBillApi } from "../../../../Api/Operations/WorkOrders";
import "../../AddContractorsAndVendors.css";

/**
 * ContractorBillForm Screen (Create Contractor Running Bill / RA Bill / IPC)
 * Designed following the layout and standards of the Client Running Bill (/client-work/invoices/add)
 * customized specifically for Contractor Work Orders and contract bills.
 */
const ContractorBillForm = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { workOrderId: paramWorkOrderId } = useParams();
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showApproverDropdown, setShowApproverDropdown] = useState(false);
  const [approverSearchTerm, setApproverSearchTerm] = useState("");
  const [approverUserIds, setApproverUserIds] = useState([]);

  // Redux state
  const allWorkOrders = useSelector((state) => state.getAllWorkOrders?.data || []);
  const workOrderDetailsState = useSelector(
    (state) => state.getWorkOrderDetails?.data?.Data || null,
  );
  const populateUsers = useSelector((state) => state.getAllPopulateUsers?.data || []);

  // Form State
  const [formData, setFormData] = useState({
    BillNumber: "",
    WorkOrderId: "",
    WorkOrderNumber: "",
    ContractorName: "",
    ProjectName: "",
    ProjectId: "",
    TotalContractValue: 0,
    BillingDate: new Date().toISOString().split("T")[0],
    PeriodFrom: new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      .toISOString()
      .split("T")[0],
    PeriodTo: new Date().toISOString().split("T")[0],
    CumulativePercentage: "",
    CumulativeGross: 0,
    PreviousGross: 0,
    RetentionRate: 5.0,
    TaxRate: 2.0,
    AdvanceAmortization: 0,
    OtherAdjustments: 0,
    AdjustmentNotes: "",
    AttachmentName: "",
    Status: "Draft",
  });

  const returnUrl = location.state?.returnUrl || "";

  // 1. Fetch all work orders on mount
  useEffect(() => {
    dispatch(getallWorkOrders());
  }, [dispatch]);

  // 2. Resolve initial Work Order from location state, param, or first available
  const initialWorkOrder = useMemo(() => {
    if (location.state?.workOrder) {
      return location.state.workOrder;
    }
    if (location.state?.Id) {
      return location.state;
    }
    return null;
  }, [location.state]);

  // If workOrderId is in param but we don't have full details, fetch it
  useEffect(() => {
    const targetId = paramWorkOrderId || initialWorkOrder?.Id;
    if (targetId && !initialWorkOrder?.Bills) {
      dispatch(getWorkOrderById(targetId));
    }
  }, [paramWorkOrderId, initialWorkOrder, dispatch]);

  // Handle setting work order data into form
  const applyWorkOrderData = useCallback(
    (wo) => {
      if (!wo) {
        return;
      }
      const contractVal = Number(wo.TotalContractValue || wo.OriginalAmount) || 0;
      const bills = wo.Bills || [];
      const maxPrevGross = bills.reduce(
        (max, b) => Math.max(max, Number(b.GrossAmountToDate) || 0),
        0,
      );
      const prevGross =
        maxPrevGross > 0
          ? maxPrevGross
          : bills.reduce((sum, b) => sum + (Number(b.CurrentMonthGross) || 0), 0);

      const prevPct =
        contractVal > 0 && prevGross > 0
          ? parseFloat(((prevGross / contractVal) * 100).toFixed(2))
          : 0;

      const nextBillNo = `${wo.WorkOrderNumber || "WO"}-B${bills.length + 1}`;

      setFormData((prev) => ({
        ...prev,
        WorkOrderId: wo.Id || wo.WorkOrderId || "",
        WorkOrderNumber: wo.WorkOrderNumber || "",
        ContractorName: wo.ContractorName || "",
        ProjectName: wo.ProjectName || "",
        ProjectId: wo.ProjectId || "",
        TotalContractValue: contractVal,
        BillNumber: nextBillNo,
        PreviousGross: prevGross,
        RetentionRate: wo.RetentionRate !== undefined ? Number(wo.RetentionRate) : 5.0,
        TaxRate: wo.TaxRate !== undefined ? Number(wo.TaxRate) : 2.0,
        CumulativePercentage: prevPct > 0 ? prevPct : "",
        CumulativeGross: prevGross > 0 ? prevGross : 0,
      }));

      if (wo.ProjectId) {
        dispatch(getPopulateUsers(wo.ProjectId));
      }
    },
    [dispatch],
  );

  // Populate from initial work order or detailed state
  useEffect(() => {
    if (initialWorkOrder) {
      applyWorkOrderData(initialWorkOrder);
    }
  }, [initialWorkOrder, applyWorkOrderData]);

  useEffect(() => {
    if (
      workOrderDetailsState &&
      (!initialWorkOrder || initialWorkOrder.Id === workOrderDetailsState.Id)
    ) {
      applyWorkOrderData(workOrderDetailsState);
    }
  }, [workOrderDetailsState, initialWorkOrder, applyWorkOrderData]);

  // Click outside listener for Approver dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowApproverDropdown(false);
      }
    }
    if (showApproverDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showApproverDropdown]);

  // Approver selection helpers
  const filteredUsers = useMemo(() => {
    return (populateUsers || []).filter(
      (user) =>
        user?.FullName?.toLowerCase().includes(approverSearchTerm.toLowerCase()) ||
        user?.Email?.toLowerCase().includes(approverSearchTerm.toLowerCase()) ||
        user?.Id?.toLowerCase().includes(approverSearchTerm.toLowerCase()),
    );
  }, [populateUsers, approverSearchTerm]);

  const removeApprover = (userId) => {
    setApproverUserIds((prev) => prev.filter((id) => id !== userId));
  };

  const toggleApprover = (userId) => {
    setApproverUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  // Handle Work Order selection change from dropdown
  const handleWorkOrderSelect = (e) => {
    const selectedId = e.target.value;
    if (!selectedId) {
      setFormData((prev) => ({
        ...prev,
        WorkOrderId: "",
        WorkOrderNumber: "",
        ContractorName: "",
        ProjectName: "",
        ProjectId: "",
        TotalContractValue: 0,
        BillNumber: "",
        PreviousGross: 0,
        CumulativePercentage: "",
        CumulativeGross: 0,
      }));
      return;
    }

    const found = allWorkOrders.find((w) => String(w.WorkOrderId || w.Id) === String(selectedId));
    if (found) {
      dispatch(getWorkOrderById(found.WorkOrderId || found.Id));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNumberChange = (name, value) => {
    const numeric = parseFloat(value) || 0;
    setFormData((prev) => ({
      ...prev,
      [name]: numeric,
    }));
  };

  // Cumulative Percentage change -> auto calculate CumulativeGross
  const handleCumulativePercentageChange = (value) => {
    const total = Number(formData.TotalContractValue) || 0;
    if (value === "") {
      setFormData((prev) => ({
        ...prev,
        CumulativePercentage: "",
        CumulativeGross: 0,
      }));
      return;
    }

    let pct = parseFloat(value);
    if (isNaN(pct)) {
      setFormData((prev) => ({
        ...prev,
        CumulativePercentage: value,
      }));
      return;
    }

    if (pct > 100) {
      pct = 100;
      value = 100;
      toast.warning("Cumulative Percentage cannot be greater than 100%");
    } else if (pct < 0) {
      pct = 0;
      value = 0;
    }

    const calculatedGross = total > 0 ? Math.round((pct / 100) * total) : 0;

    setFormData((prev) => ({
      ...prev,
      CumulativePercentage: value,
      CumulativeGross: calculatedGross,
    }));
  };

  // Cumulative Gross change -> auto calculate CumulativePercentage
  const handleCumulativeGrossChange = (value) => {
    const total = Number(formData.TotalContractValue) || 0;
    if (value === "") {
      setFormData((prev) => ({
        ...prev,
        CumulativeGross: "",
        CumulativePercentage: "",
      }));
      return;
    }

    let gross = parseFloat(value);
    if (isNaN(gross)) {
      setFormData((prev) => ({
        ...prev,
        CumulativeGross: value,
      }));
      return;
    }

    if (gross < 0) {
      gross = 0;
      value = 0;
    }

    if (total > 0 && gross > total) {
      gross = total;
      value = total;
      toast.warning(
        `Cumulative Gross amount cannot exceed Total Contract Value (PKR ${total.toLocaleString()})`,
      );
    }

    const calculatedPct = total > 0 ? parseFloat(((gross / total) * 100).toFixed(2)) : "";

    setFormData((prev) => ({
      ...prev,
      CumulativeGross: value,
      CumulativePercentage:
        calculatedPct !== "" && Number(calculatedPct) > 100 ? 100 : calculatedPct,
    }));
  };

  // Dynamic Current Gross Claimed = Cumulative Gross - Previous Gross
  const currentGrossClaimed = useMemo(() => {
    const cumulative = Number(formData.CumulativeGross) || 0;
    const previous = Number(formData.PreviousGross) || 0;
    return Math.max(0, cumulative - previous);
  }, [formData.CumulativeGross, formData.PreviousGross]);

  // Retention amount based on RetentionRate
  const retentionAmount = useMemo(() => {
    const rate = Number(formData.RetentionRate) || 0;
    return Math.round((currentGrossClaimed * rate) / 100);
  }, [currentGrossClaimed, formData.RetentionRate]);

  // Tax amount based on TaxRate
  const taxAmount = useMemo(() => {
    const rate = Number(formData.TaxRate) || 0;
    return Math.round((currentGrossClaimed * rate) / 100);
  }, [currentGrossClaimed, formData.TaxRate]);

  // Total Deductions
  const totalDeductions = useMemo(() => {
    const ret = retentionAmount;
    const tax = taxAmount;
    const adv = Number(formData.AdvanceAmortization) || 0;
    const oth = Number(formData.OtherAdjustments) || 0;
    return ret + tax + adv + oth;
  }, [retentionAmount, taxAmount, formData.AdvanceAmortization, formData.OtherAdjustments]);

  // Net Payable to Contractor
  const netPayable = useMemo(() => {
    return Math.max(0, currentGrossClaimed - totalDeductions);
  }, [currentGrossClaimed, totalDeductions]);

  // Previous Percentage calculated for benchmark display
  const previousPercentage = useMemo(() => {
    const total = Number(formData.TotalContractValue) || 0;
    const prev = Number(formData.PreviousGross) || 0;
    return total > 0 && prev > 0 ? ((prev / total) * 100).toFixed(2) : "0.00";
  }, [formData.TotalContractValue, formData.PreviousGross]);

  // File upload handler
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setFormData((prev) => ({
        ...prev,
        AttachmentName: file.name,
      }));
      toast.success(`Attached "${file.name}"`);
    }
  };

  // Cancellation
  const handleCancel = () => {
    if (returnUrl) {
      history.push(returnUrl);
    } else if (formData.WorkOrderId) {
      history.push(`/works/work-orders/details/${formData.WorkOrderId}`);
    } else {
      history.push("/works/contractor-bills");
    }
  };

  // Submit Handler
  const handleSubmit = async () => {
    if (!formData.WorkOrderId) {
      toast.error("Please select a linked Work Order");
      return;
    }
    if (!formData.CumulativePercentage || Number(formData.CumulativePercentage) <= 0) {
      toast.error("Cumulative Work Done (%) is required and must be greater than 0");
      return;
    }
    if (Number(formData.CumulativePercentage) > 100) {
      toast.error("Cumulative Work Done (%) cannot exceed 100%");
      return;
    }
    if (Number(formData.CumulativePercentage) < Number(previousPercentage)) {
      toast.warning(
        `Cumulative Work Done (${formData.CumulativePercentage}%) is lower than previous certified progress (${previousPercentage}%).`,
      );
    }
    const contractVal = Number(formData.TotalContractValue) || 0;
    if (contractVal > 0 && Number(formData.CumulativeGross) > contractVal) {
      toast.error(
        `Cumulative Gross cannot exceed Total Contract Value (PKR ${contractVal.toLocaleString()})`,
      );
      return;
    }

    setIsLoading(true);
    try {
      const fd = new FormData();
      fd.append("workOrderId", formData.WorkOrderId);
      fd.append("cumulativePercentage", Number(formData.CumulativePercentage));
      if (selectedFile) {
        fd.append("image", selectedFile);
        fd.append("file", selectedFile);
      }
      approverUserIds.forEach((id, index) => {
        fd.append(`ApproverUserIds[${index}]`, id);
      });

      // Additional metadata for backend audit and reports
      fd.append("billNumber", formData.BillNumber || "");
      fd.append("billingDate", formData.BillingDate || "");
      fd.append("periodFrom", formData.PeriodFrom || "");
      fd.append("periodTo", formData.PeriodTo || "");
      fd.append("cumulativeGross", Number(formData.CumulativeGross) || 0);
      fd.append("previousGross", Number(formData.PreviousGross) || 0);
      fd.append("currentGross", currentGrossClaimed);
      fd.append("retentionRate", Number(formData.RetentionRate) || 0);
      fd.append("retentionAmount", retentionAmount);
      fd.append("taxRate", Number(formData.TaxRate) || 0);
      fd.append("taxAmount", taxAmount);
      fd.append("advanceAmortization", Number(formData.AdvanceAmortization) || 0);
      fd.append("otherAdjustments", Number(formData.OtherAdjustments) || 0);
      fd.append("netPayable", netPayable);
      fd.append("notes", formData.AdjustmentNotes || "");

      const res = await AddBillApi(fd);
      const json = res?.data || res;
      if (json?.IsSuccess) {
        toast.success(json.Message || "Contractor bill created successfully");
        if (returnUrl) {
          history.push(returnUrl);
        } else {
          history.push(`/works/work-orders/details/${formData.WorkOrderId}`);
        }
      } else {
        toast.error(json?.Message || "Failed to create contractor bill");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error creating contractor bill");
    } finally {
      setIsLoading(false);
    }
  };

  const workOrderOptions = useMemo(() => {
    return [
      { value: "", label: "Select Contractor Work Order" },
      ...allWorkOrders.map((wo) => ({
        value: wo.WorkOrderId || wo.Id,
        label: `${wo.WorkOrderNumber || "WO"} — ${wo.ProjectName || "Project"} (${wo.ContractorName || "Contractor"})`,
      })),
    ];
  }, [allWorkOrders]);

  return (
    <div className="vendor-screen-wrapper app_font">
      {/* ── Page Header ── */}
      <div className="col-md-12 text-left page_heading d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <FaArrowLeft
            size={16}
            style={{ cursor: "pointer", marginRight: "12px" }}
            title="Back"
            onClick={handleCancel}
          />
          <h4 className="m-0">Create Contractor Running Bill (RA Bill / IPC)</h4>
        </div>
      </div>

      <form onSubmit={(e) => e.preventDefault()} autoComplete="off">
        {/* ── CARD 1: Bill Identification & Context ── */}
        <div className="vendor-card">
          <h5 className="vendor-card-title">Bill Identification & Context</h5>

          <div className="row mb-2">
            <InputField
              label="Bill Number:"
              name="BillNumber"
              value={formData.BillNumber || "Auto-generated"}
              disabled
              placeholder="WO-0001-B1"
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            >
              <small className="text-muted d-block mt-1" style={{ fontSize: "11px" }}>
                Auto-assigned progressive bill identifier
              </small>
            </InputField>

            <SelectField
              label="Linked Work Order:"
              name="WorkOrderId"
              value={formData.WorkOrderId}
              onChange={handleWorkOrderSelect}
              options={workOrderOptions}
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            >
              <small className="text-muted d-block mt-1" style={{ fontSize: "11px" }}>
                Total Contract Value: PKR{" "}
                {Number(formData.TotalContractValue || 0).toLocaleString()}
              </small>
            </SelectField>

            <InputField
              label="Contractor Name:"
              name="ContractorName"
              value={formData.ContractorName}
              disabled
              placeholder="e.g. Tariq Builders & Co."
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />

            <InputField
              label="Billing Date:"
              name="BillingDate"
              type="date"
              value={formData.BillingDate}
              onChange={handleInputChange}
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />

            <InputField
              label="Project Name:"
              name="ProjectName"
              value={formData.ProjectName}
              disabled
              placeholder="e.g. Gulberg Heights Project"
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />

            <div className="col-xl-3 col-lg-4 col-md-6 col-12 stacked-form-field mb-3">
              <label className="input_label d-block m-0 mb-2">
                Measurement Period:<span className="estaric text-danger ml-1">*</span>
              </label>
              <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                <input
                  type="date"
                  name="PeriodFrom"
                  value={formData.PeriodFrom}
                  onChange={handleInputChange}
                  className="input_styling m-0"
                  required
                />
                <span className="text-muted font-weight-bold">to</span>
                <input
                  type="date"
                  name="PeriodTo"
                  value={formData.PeriodTo}
                  onChange={handleInputChange}
                  className="input_styling m-0"
                  required
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── CARD 2: Progress Valuation (Cumulative & Current Gross Work) ── */}
        <div className="vendor-card">
          <h5 className="vendor-card-title">
            Progress Valuation (Cumulative & Current Gross Work)
          </h5>

          {/* Direct Input Warning / Notice Banner */}
          <div
            className="d-flex align-items-center p-2 mb-3"
            style={{
              backgroundColor: "#fffbeb",
              border: "1px solid #fde68a",
              borderRadius: "6px",
              color: "#92400e",
              fontSize: "12px",
            }}
          >
            <FaInfoCircle className="mr-2 flex-shrink-0" size={15} color="#d97706" />
            <span>
              <strong>Direct Input Active:</strong> Contractor Bill is calculated based on work
              order progress percentage and progressive certified gross work.
            </span>
          </div>

          {/* 4 Metric Columns in Row */}
          <div className="row">
            <InputField
              label="Cumulative Work Done (%):"
              name="CumulativePercentage"
              type="number"
              step="any"
              min="0"
              max="100"
              value={formData.CumulativePercentage}
              onChange={(e) => handleCumulativePercentageChange(e.target.value)}
              placeholder="25.00"
              required
              stacked
              wrapperCol="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12"
            >
              <span
                className="position-absolute text-muted font-weight-bold"
                style={{ right: "12px", top: "50%", transform: "translateY(-50%)" }}
              >
                %
              </span>
              <small className="text-muted d-block mt-1" style={{ fontSize: "11px" }}>
                % of Work Order (PKR {Number(formData.TotalContractValue || 0).toLocaleString()})
              </small>
            </InputField>

            <InputField
              label="Cumulative Work Done (Gross):"
              name="CumulativeGross"
              type="number"
              min="0"
              max={Number(formData.TotalContractValue) || undefined}
              value={formData.CumulativeGross}
              onChange={(e) => handleCumulativeGrossChange(e.target.value)}
              placeholder="500000"
              required
              stacked
              wrapperCol="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12"
            >
              <small className="text-muted d-block mt-1" style={{ fontSize: "11px" }}>
                Total gross work executed to this date
              </small>
            </InputField>

            <InputField
              label="Less: Previously Billed Work (Gross):"
              name="PreviousGross"
              type="number"
              value={formData.PreviousGross}
              onChange={(e) => handleNumberChange("PreviousGross", e.target.value)}
              placeholder="0"
              required
              stacked
              wrapperCol="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12"
            >
              <small className="text-muted d-block mt-1" style={{ fontSize: "11px" }}>
                Gross value certified in previous bills ({previousPercentage}% to date)
              </small>
            </InputField>

            <InputField
              label="Gross Work Done (Current):"
              name="CurrentGrossClaimed"
              value={`PKR ${currentGrossClaimed.toLocaleString()}`}
              disabled
              stacked
              wrapperCol="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12"
            >
              <small className="text-muted d-block mt-1" style={{ fontSize: "11px" }}>
                Based on: Cumulative - Previous
              </small>
            </InputField>
          </div>
        </div>

        {/* ── CARD 3: Deductions & Adjustments Stack ── */}
        <div className="vendor-card">
          <h5 className="vendor-card-title">Deductions & Adjustments Stack</h5>

          <div className="row">
            {/* Left Column: Adjustment Notes, Attachment & Approvers */}
            <div className="col-md-6 pr-md-4">
              <div className="row">
                <TextAreaField
                  label="Adjustment Notes & Justifications:"
                  name="AdjustmentNotes"
                  rows={3}
                  value={formData.AdjustmentNotes}
                  onChange={handleInputChange}
                  placeholder="e.g. RA Bill #2. Certified 45% completion of structure and masonry work. Subject to standard retention deduction."
                  stacked
                  wrapperCol="col-lg-6 col-md-12 col-12"
                />

                <div className="col-lg-6 col-md-12 col-12 stacked-form-field mb-3">
                  <label className="input_label d-block m-0 mb-2">
                    Measurement Document / Bill Image:
                  </label>
                  <div
                    className="document-upload-box"
                    role="button"
                    tabIndex={0}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        fileInputRef.current?.click();
                      }
                    }}
                  >
                    <BsCloudUpload className="document-upload-icon" />
                    {formData.AttachmentName ? (
                      <div className="document-upload-file">
                        <BsFileEarmarkPdf className="text-danger flex-shrink-0" />
                        <span title={formData.AttachmentName}>{formData.AttachmentName}</span>
                        <button
                          type="button"
                          className="document-upload-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFile(null);
                            setFormData((prev) => ({ ...prev, AttachmentName: "" }));
                            if (fileInputRef.current) {
                              fileInputRef.current.value = "";
                            }
                          }}
                          title="Remove document"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className="document-upload-copy">
                        <strong>Click to upload</strong>
                        <span>Progress sheet or measurement photo</span>
                      </div>
                    )}
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="d-none"
                      onChange={handleFileUpload}
                      accept=".pdf,.png,.jpg,.jpeg,.docx"
                    />
                  </div>
                </div>
              </div>

              {/* Approver Users selection */}
              <div className="form-group mb-2" style={{ position: "relative" }} ref={dropdownRef}>
                <label className="input_label m-0 font-weight-bold" style={{ fontSize: "12.5px" }}>
                  Approver Users (Workflow):
                </label>
                <div
                  className="input_styling w-100 mt-2"
                  style={{
                    minHeight: "44px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "4px",
                    alignItems: "start",
                    cursor: "pointer",
                    padding: "6px 8px",
                    boxSizing: "border-box",
                    overflow: "auto",
                    maxHeight: "100px",
                  }}
                  onClick={() => setShowApproverDropdown((prev) => !prev)}
                >
                  {approverUserIds.length === 0 ? (
                    <span style={{ color: "#6c757d", fontSize: "13px", lineHeight: "28px" }}>
                      Select approvers...
                    </span>
                  ) : (
                    approverUserIds.map((userId) => {
                      const user = populateUsers?.find((u) => u?.Id === userId);
                      return (
                        <span
                          key={userId}
                          style={{
                            backgroundColor: "#e7f4f4",
                            color: "#0b6462",
                            padding: "3px 8px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            whiteSpace: "nowrap",
                            fontWeight: 600,
                          }}
                        >
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "inline-block",
                              maxWidth: "140px",
                            }}
                          >
                            {user ? user.FullName : userId}
                          </span>
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              removeApprover(userId);
                            }}
                            style={{
                              cursor: "pointer",
                              color: "#dc3545",
                              fontWeight: "bold",
                            }}
                          >
                            ×
                          </span>
                        </span>
                      );
                    })
                  )}
                </div>

                {showApproverDropdown && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      zIndex: 1050,
                      backgroundColor: "white",
                      border: "1px solid #ced4da",
                      borderRadius: "6px",
                      boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                      marginTop: "4px",
                      maxHeight: "220px",
                      overflowY: "auto",
                    }}
                  >
                    <div style={{ padding: "8px", borderBottom: "1px solid #e9ecef" }}>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search approvers..."
                        value={approverSearchTerm}
                        onChange={(e) => setApproverSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    </div>
                    {filteredUsers.length === 0 ? (
                      <div className="p-3 text-muted text-center" style={{ fontSize: "12px" }}>
                        No approvers found
                      </div>
                    ) : (
                      filteredUsers.map((user) => {
                        const isSelected = approverUserIds.includes(user.Id);
                        return (
                          <div
                            key={user.Id}
                            style={{
                              padding: "8px 12px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              backgroundColor: isSelected ? "#e7f4f4" : "white",
                              borderBottom: "1px solid #f1f5f9",
                            }}
                            onClick={() => toggleApprover(user.Id)}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}}
                              style={{ cursor: "pointer" }}
                            />
                            <div style={{ fontSize: "12.5px" }}>
                              <span style={{ fontWeight: 600 }}>{user.FullName}</span>
                              {user.Email && (
                                <small className="text-muted d-block" style={{ fontSize: "11px" }}>
                                  {user.Email}
                                </small>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
                <small className="text-muted d-block mt-1" style={{ fontSize: "10.5px" }}>
                  Selected users will review and approve this contractor bill
                </small>
              </div>
            </div>

            {/* Right Column: Financial Deductions Stack */}
            <div className="col-md-6 pl-md-4 border-left">
              {/* Current Gross Claimed */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span style={{ fontWeight: 600, color: "#334155", fontSize: "13.5px" }}>
                  Current Gross Claimed:
                </span>
                <span style={{ fontWeight: 700, color: "#0f172a", fontSize: "15px" }}>
                  PKR {currentGrossClaimed.toLocaleString()}
                </span>
              </div>

              {/* Less: Retention Money */}
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "12.5px", fontWeight: 500, color: "#475569" }}>
                    Less: Retention Money <span className="estaric text-danger">*</span>
                  </div>
                  <small className="text-muted" style={{ fontSize: "10.5px" }}>
                    (autocomputed as {formData.RetentionRate}% of gross)
                  </small>
                </div>
                <div className="d-flex align-items-center" style={{ gap: "6px" }}>
                  <div className="d-flex align-items-center" style={{ width: "70px" }}>
                    <input
                      type="number"
                      step="0.1"
                      className="input_styling text-right pr-1"
                      style={{ width: "45px", height: "28px", fontSize: "12px", padding: "0 4px" }}
                      value={formData.RetentionRate}
                      onChange={(e) => handleNumberChange("RetentionRate", e.target.value)}
                    />
                    <span className="ml-1" style={{ fontSize: "11px", color: "#64748b" }}>
                      %
                    </span>
                  </div>
                  <div
                    className="d-flex align-items-center justify-content-between px-2"
                    style={{
                      width: "140px",
                      height: "28px",
                      backgroundColor: "#f8fafc",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: 600,
                    }}
                  >
                    <span style={{ fontSize: "10.5px", color: "#64748b" }}>PKR</span>
                    <span>{retentionAmount.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Less: Mobilization / Advance Recovery */}
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "12.5px", fontWeight: 500, color: "#475569" }}>
                    Less: Mobilization Advance Recovery
                  </div>
                  <small className="text-muted" style={{ fontSize: "10.5px" }}>
                    (deductible as per work order agreement)
                  </small>
                </div>
                <div
                  className="d-flex align-items-center"
                  style={{ width: "216px", justifyContent: "flex-end" }}
                >
                  <div
                    className="d-flex align-items-center px-1"
                    style={{
                      width: "140px",
                      height: "28px",
                      backgroundColor: "#fff",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                    }}
                  >
                    <span className="text-muted mr-1" style={{ fontSize: "10.5px" }}>
                      PKR
                    </span>
                    <input
                      type="number"
                      className="border-0 w-100 text-right pr-1 font-weight-bold"
                      style={{ outline: "none", fontSize: "12px" }}
                      value={formData.AdvanceAmortization}
                      onChange={(e) => handleNumberChange("AdvanceAmortization", e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Less: Tax / WHT */}
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "12.5px", fontWeight: 500, color: "#475569" }}>
                    Less: Income Tax / WHT
                  </div>
                  <small className="text-muted" style={{ fontSize: "10.5px" }}>
                    (withholding tax as per contract rate {formData.TaxRate}%)
                  </small>
                </div>
                <div className="d-flex align-items-center" style={{ gap: "6px" }}>
                  <div className="d-flex align-items-center" style={{ width: "70px" }}>
                    <input
                      type="number"
                      step="0.1"
                      className="input_styling text-right pr-1"
                      style={{ width: "45px", height: "28px", fontSize: "12px", padding: "0 4px" }}
                      value={formData.TaxRate}
                      onChange={(e) => handleNumberChange("TaxRate", e.target.value)}
                    />
                    <span className="ml-1" style={{ fontSize: "11px", color: "#64748b" }}>
                      %
                    </span>
                  </div>
                  <div
                    className="d-flex align-items-center justify-content-between px-2"
                    style={{
                      width: "140px",
                      height: "28px",
                      backgroundColor: "#f8fafc",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: 600,
                    }}
                  >
                    <span style={{ fontSize: "10.5px", color: "#64748b" }}>PKR</span>
                    <span>{taxAmount.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Other Adjustments / Deductions */}
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "12.5px", fontWeight: 500, color: "#475569" }}>
                    Other Adjustments / Penalties
                  </div>
                  <small
                    className="text-info cur_sor"
                    style={{ fontSize: "10.5px", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() =>
                      toast.info(
                        "Deduction heads: site electricity, safety non-compliance, test penalties, etc.",
                      )
                    }
                  >
                    Info on deduction heads
                  </small>
                </div>
                <div
                  className="d-flex align-items-center"
                  style={{ width: "216px", justifyContent: "flex-end" }}
                >
                  <div
                    className="d-flex align-items-center px-1"
                    style={{
                      width: "140px",
                      height: "28px",
                      backgroundColor: "#fff",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                    }}
                  >
                    <span className="text-muted mr-1" style={{ fontSize: "10.5px" }}>
                      PKR
                    </span>
                    <input
                      type="number"
                      className="border-0 w-100 text-right pr-1 font-weight-bold"
                      style={{ outline: "none", fontSize: "12px" }}
                      value={formData.OtherAdjustments}
                      onChange={(e) => handleNumberChange("OtherAdjustments", e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Total Deductions Summary Row */}
              <div
                className="d-flex justify-content-between align-items-center py-2 px-3 mb-3"
                style={{
                  backgroundColor: "#f8fafc",
                  borderRadius: "6px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 600 }}>
                  Total Deductions & Recoveries:
                </span>
                <span style={{ fontSize: "13px", color: "#dc2626", fontWeight: 700 }}>
                  - PKR {totalDeductions.toLocaleString()}
                </span>
              </div>

              {/* Net Payable Banner */}
              <div
                className="d-flex justify-content-between align-items-center p-3"
                style={{
                  backgroundColor: "#ecfdf5",
                  border: "1.5px solid #a7f3d0",
                  borderRadius: "8px",
                }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: "13px", color: "#065f46" }}>
                    Net Payable (To Contractor):
                  </div>
                  <small style={{ color: "#047857", fontSize: "11px" }}>
                    Gross Claimed minus all progressive withholdings & deductions
                  </small>
                </div>
                <div style={{ fontWeight: 800, fontSize: "18px", color: "#047857" }}>
                  PKR {netPayable.toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Bottom Actions Bar ── */}
        <div className="col-12 p-0">
          <div className="float-right mt-2 mb-4 d-flex align-items-center" style={{ gap: "10px" }}>
            <ActionButton
              text="Cancel"
              variant="cancel"
              type="button"
              onClick={handleCancel}
              disabled={isLoading}
            />
            <ActionButton
              text={isLoading ? "Submitting..." : "Submit Bill"}
              variant="save"
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default ContractorBillForm;
